import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material'
import {Box, IconButton, InputAdornment, Theme} from '@mui/material'
import {CalendarPickerView, PickersDay, PickersDayProps} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {dataTestId} from '../../../common'

import {calculateWeekRange} from './calculateWeekRange'
import {CalendarDatePickerRange} from './CalendarDatePickerRange'
import {CalendarInput} from './CalendarInput'

const getCalendarViews = (type: CalendarDatePickerRange): CalendarPickerView[] => {
  switch (type) {
    case CalendarDatePickerRange.Monthly: {
      return ['month', 'year']
    }
    case CalendarDatePickerRange.Daily:
    case CalendarDatePickerRange.Weekly: {
      return ['day']
    }
    case CalendarDatePickerRange.Yearly: {
      return ['year']
    }
  }
}

const getArrowColor = (theme: Theme, contrast?: boolean) => {
  if (!contrast) {
    return theme.palette.common.white
  }

  return theme.palette.getContrastText(theme.palette.background.default)
}

export const CalendarDatePicker = ({
  label,
  selectedDate,
  contrastingText,
  timeZone,
  setSelectedDate,
  type,
  fullWidth,
  minDateAllowed,
  maxDateAllowed
}: {
  label: string
  contrastingText?: boolean
  selectedDate: string | undefined
  timeZone: string
  setSelectedDate: (date: string | undefined) => void
  type: CalendarDatePickerRange
  fullWidth?: boolean
  minDateAllowed?: Moment
  maxDateAllowed?: Moment
}) => {
  const {
    i18n: {language}
  } = useTranslation()
  const [calendarIsOpen, setCalendarIsOpen] = useState<boolean>(false)

  const disablePreviousDate = useMemo(() => {
    const selected = moment(selectedDate)
    return minDateAllowed && selected.isSameOrBefore(minDateAllowed, type)
  }, [minDateAllowed, selectedDate, type])

  const minDate = useMemo(() => minDateAllowed?.toDate(), [minDateAllowed])
  const disableNextDate = useMemo(() => {
    const newestPossibleDate = maxDateAllowed ?? moment.tz(timeZone).subtract(1, 'day')
    const selected = moment(selectedDate)
    return selected.isSameOrAfter(newestPossibleDate, type)
  }, [maxDateAllowed, timeZone, selectedDate, type])

  const maxDate = useMemo(
    () =>
      (maxDateAllowed && maxDateAllowed.toDate()) ||
      new Date(new Date().getTime() - 24 * 60 * 60 * 1000),
    [maxDateAllowed]
  )

  const renderDay = (
    date: Date,
    _selectedDates: Array<Date | null>,
    pickersDayProps: PickersDayProps<Date>
  ) => {
    const dateIsInTheSameWeek = moment(selectedDate, 'YYYYMMDD').isSame(
      new Date(pickersDayProps.day).toISOString(),
      'week'
    )

    const isStart = moment(date).weekday() === 0
    const isEnd = moment(date).weekday() === 6

    if (dateIsInTheSameWeek) {
      return (
        <PickersDay
          {...pickersDayProps}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.light,
            color: 'white',
            borderTopRightRadius: isEnd ? 8 : 0,
            borderBottomRightRadius: isEnd ? 8 : 0,
            borderTopLeftRadius: isStart ? 8 : 0,
            borderBottomLeftRadius: isStart ? 8 : 0,
            paddingX: 2.5,
            margin: 0
          }}
          day={date}
        />
      )
    }

    return (
      <PickersDay
        {...pickersDayProps}
        day={date}
        sx={{borderRadius: 0, paddingX: 2.5, margin: 0}}
      />
    )
  }

  const calendarViews = getCalendarViews(type)

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language}>
      <DatePicker
        label={label}
        value={selectedDate}
        views={calendarViews}
        onChange={(date) => setSelectedDate(date?.toISOString().split('T')[0])}
        open={calendarIsOpen}
        onOpen={() => setCalendarIsOpen(true)}
        onClose={() => setCalendarIsOpen(false)}
        renderDay={renderDay}
        renderInput={(props) => (
          <CalendarInput
            {...props}
            fullWidth={fullWidth}
            contrast={contrastingText}
            inputProps={{
              ...props.inputProps,
              readOnly: true,
              ...(type === CalendarDatePickerRange.Weekly && {
                value: `CW${moment(selectedDate).week()} (${calculateWeekRange(selectedDate)})`
              })
            }}
            InputProps={{
              ...props.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{paddingLeft: 0, marginLeft: 0}}>
                  <IconButton
                    {...dataTestId('calendar-picker-left-arrow')}
                    sx={{
                      borderRadius: 0,
                      color: (theme) => getArrowColor(theme, contrastingText)
                    }}
                    size="large"
                    onClick={(event) => {
                      event.stopPropagation()
                      if (!disablePreviousDate) {
                        const date =
                          selectedDate !== undefined && selectedDate !== null
                            ? moment(selectedDate).startOf('day')
                            : moment.tz(timeZone).subtract(1, 'days').startOf('day')
                        const newDate = date.subtract(1, type).format('YYYY-MM-DD')
                        setSelectedDate(newDate)
                      }
                    }}
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {paddingX: 0},
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    {...dataTestId('calendar-picker-right-arrow')}
                    sx={{
                      borderRadius: 0,
                      color: (theme) => getArrowColor(theme, contrastingText)
                    }}
                    size="large"
                    onClick={(event) => {
                      event.stopPropagation()
                      if (!disableNextDate) {
                        const date =
                          moment(selectedDate).startOf('day') ??
                          moment.tz(timeZone).subtract(1, 'days').startOf('day')

                        const newDate = date.add(1, type).startOf('day').format('YYYY-MM-DD')
                        setSelectedDate(newDate)
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        justifyItems: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        height: '100%'
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </Box>
                  </IconButton>
                </InputAdornment>
              )
            }}
            onClick={() => setCalendarIsOpen(true)}
            {...dataTestId(`calendar-date-picker-${type}`)}
          />
        )}
        maxDate={maxDate}
        minDate={minDate}
      />
    </LocalizationProvider>
  )
}
